import {
  Grid,
  Progress,
  Radio,
  TextInput,
  FileInput,
  Modal,
  Slider,
  Select,
  Tabs,
  createTheme,
  Center,
  PasswordInput,
  Paper,
  NumberInput,
} from '@mantine/core';
import progressClasses from '@/assets/styles/mantine/progress.module.css';
import textInputClasses from '@/assets/styles/mantine/textInput.module.css';
import fileInputClasses from '@/assets/styles/mantine/fileInput.module.css';
import numberInputClasses from '@/assets/styles/mantine/numberInput.module.css';
import radioClasses from '@/assets/styles/mantine/radio.module.css';
import modalClasses from '@/assets/styles/mantine/modal.module.css';
import sliderClasses from '@/assets/styles/mantine/slider.module.css';
import selectClasses from '@/assets/styles/mantine/select.module.css';
import tabsClasses from '@/assets/styles/mantine/tabs.module.css';
import centerClasses from '@/assets/styles/mantine/center.module.css';
import passwordInputClasses from '@/assets/styles/mantine/passwordInput.module.css';
import paperClasses from '@/assets/styles/mantine/paper.module.css';
import buttonClasses from '@/assets/styles/mantine/button.module.css';

const mantineTheme = {
  fontFamily: 'Rubik, Arial',
  fontSizes: {
    xs: '0.5rem',
    sm: '0.75rem',
    md: '1rem',
    lg: '1.25rem',
    xl: '1.5rem',
  },
  black: 'black',
  white: '#fff',
  cursorType: 'pointer',
  primaryColor: 'brand',
  colors: {
    brand: [
      '#edebfa',
      '#cdc8f1',
      '#b6afed',
      '#8e83e1',
      '#6c5dd6',
      '#5646d1',
      '#4b3acf',
      '#3c2cb7',
      '#3427a5',
      '#2a2092',
    ],
    secondary: [
      '#d0ebd5',
      '#b4dfbb',
      '#97d2a1',
      '#7bc688',
      '#8692b2', // main
      '#48a758',
      '#3c8b49',
      '#2f6e3a',
      '#23522b',
      '#17351c',
    ],
    gray: [
      '#F8F7FC', // Lightest background, cards
      '#E3E1EC', // Base color (subtle borders)
      '#D0CDDD', // Dividers, hover states
      '#888D9F', // Secondary text, icons
      '#A9A5BF', // Disabled elements
      '#9592AA', // Subtle borders
      '#7B7894', // Primary text (light mode)
      '#615E7A', // Headings, emphasized text
      '#47445F', // Dark backgrounds (dark mode)
      '#2D2A45', // High-contrast text
      '#DCE0E5',
      '#6f7c8e', // table headers
      '#181C29',
    ],
    radio: [
      '#9F1FF4', // Border color - investmentProperty
      '#F58700', // Border color - replacementProperty
      '#6F7C8E', // Border color - renovationOrExpansion
      '#3348FF', // Border color - singleProperty
      '#FF527A', // Border color - anyPurposeWithPropertyCollateral
      '#00BD1F', // Border color - mortgageRefinancing
      '#00ABAB', // Border color - reverseMortgage
      '#1C2EB0', // Border color - businessCredit
      '#F6EBFF', // Background color - investmentProperty
      '#FFF6D1', // Background color - replacementProperty
      '#EDF0F2', // Background color - renovationOrExpansion
      '#F0F7FF', // Background color - singleProperty
      '#FFF0F1', // Background color - anyPurposeWithPropertyCollateral
      '#DBFFE1', // Background color - mortgageRefinancing
      '#D4FCF3', // Background color - reverseMortgage
      '#F0F7FF', // Background color - businessCredit
    ],
    blue: [
      '#f0f7ff',
      '#e0edff',
      '#C1CBFF',
      '#7785FF',
      '#3348FF', // main
      '#2D42EB',
      '#283BD8',
      '#2235C4',
      '#1C2EB0', // main dark
    ],
  },
  components: {
    Progress: Progress.extend({ classNames: progressClasses }),
    Grid: Grid.extend({ defaultProps: { gutter: 'xl' } }),
    FileInput: FileInput.extend({
      classNames: fileInputClasses,
    }),
    TextInput: TextInput.extend({
      classNames: textInputClasses,
      defaultProps: {
        placeholder: '',
        autoComplete: 'off',
        inputWrapperOrder: ['label', 'input', 'error', 'description'],
      },
    }),
    Paper: Paper.extend({
      classNames: paperClasses,
      defaultProps: {
        withBorder: true,
        px: '3rem',
        radius: 'md',
        py: 'md',
        mb: 'lg',
      },
    }),
    Radio: Radio.extend({ classNames: radioClasses }),
    Button: Radio.extend({
      classNames: buttonClasses,
      defaultProps: { size: 'md', radius: 'sm' },
    }),
    Modal: Modal.extend({ classNames: modalClasses }),
    Slider: Slider.extend({ classNames: sliderClasses }),
    Select: Select.extend({ classNames: selectClasses, defaultProps: { allowDeselect: false } }),
    Tabs: Tabs.extend({ classNames: tabsClasses }),
    Center: Center.extend({ classNames: centerClasses }),
    PasswordInput: PasswordInput.extend({ classNames: passwordInputClasses }),
    NumberInput: NumberInput.extend({ classNames: numberInputClasses }),
  },
};

const resolver = () => ({
  variables: {
    '--mantine-color-body': '#F5F6FA', // TODO: why its not override?
    '--f-color-body': '#F5F6FA',
    '--f-color-text': '#0A3238',
    '--f-color-brand': 'var(--mantine-color-brand-6)',
    '--f-color-secondary': 'var(--mantine-color-secondary-4)',
    '--f-page-padding-inline': '20px',
    '--f-page-head-height': '100px',
    '--f-aside-width': '70px',
    '--f-aside-width-open': '200px',
    '--f-aside-spacing': '8px',
    '--f-color-danger': '#ff0000',
    '--f-color-link': 'var(--mantine-color-brand-5)',
    '--f-page-part-width-xs': '80px',
    '--f-page-part-width-sm': '140px',
    '--f-page-part-width-md': '200px',
    '--f-page-part-width-lg': '260px',
    '--f-page-part-width-xl': '320px',
  },
});

const dates = {
  locale: 'he',
  firstDayOfWeek: 0, // sunday
  timezone: 'Asia/Jerusalem',
};

const theme = createTheme(mantineTheme);

export {
  theme,
  resolver,
  dates,
};
